import { useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Lock } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    }
    setSaving(false);
  };

  return (
    <div data-testid="settings-page" className="space-y-6 max-w-lg">
      <div className="animate-fade-in-up">
        <h1 className="font-serif text-3xl font-semibold text-foreground">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your account</p>
      </div>

      <Card className="luxury-card rounded-sm">
        <CardHeader>
          <CardTitle className="font-serif text-lg flex items-center gap-2">
            <Lock className="w-4 h-4 gold-text" /> Change Password
          </CardTitle>
          <CardDescription className="text-muted-foreground text-sm">
            Update your admin password
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Current Password</Label>
              <Input
                data-testid="current-password-input"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="bg-background border-border rounded-sm"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">New Password</Label>
              <Input
                data-testid="new-password-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="bg-background border-border rounded-sm"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Confirm New Password</Label>
              <Input
                data-testid="confirm-password-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="bg-background border-border rounded-sm"
                required
              />
            </div>
            <Button data-testid="change-password-btn" type="submit" disabled={saving} className="btn-maroon rounded-sm w-full">
              {saving ? "Changing..." : "Change Password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* App Info */}
      <Card className="luxury-card rounded-sm">
        <CardHeader>
          <CardTitle className="font-serif text-lg">About</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Application</span>
            <span className="text-foreground">VR The Darzi Studio</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">WhatsApp</span>
            <span className="text-amber-400 text-xs">MOCKED (Configure API keys)</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Birthday Scheduler</span>
            <span className="text-green-400 text-xs">Active (24h interval)</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
